import React, { useContext, useState } from "react";
import {
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { CommonContext } from "../../context/CommonContext";
import { authHttp } from "../../services/commonHttpService";
import { apisPath, paths } from "../../utils/path";
import { Formik, Form } from "formik";

//  Import schema and initial values
import { patientRegistrationSchema } from "../../schema/patientRegistrationSchema";
import { getPatientRegistrationInitialValues } from "../../schema/patientRegistrationInitialValues";
import { formatAbhaId } from "../../helpers/commonHelper";

const PatientRegistration = () => {
  const [editable, setEditable] = useState(true);
  const { abhaProfile } = useContext(AuthContext);
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  // Text-safe helper for optional fields
  const getDuration = (duration) => {
    const num = Number(duration);
    return !isNaN(num) && duration !== "" ? num : null;
  };

  // Config arrays (UI structure)
  const habitsConfig = [
    { key: "smoking", label: "Smoking" },
    { key: "smokeless", label: "Smokeless" },
    { key: "betelnut_with_tobacco", label: "Betelnut With Tobacco" },
    { key: "alcohol", label: "Alcohol" },
  ];

  const comorbiditiesConfig = [
    { key: "diabetes", label: "Diabetes" },
    { key: "hypertension", label: "Hypertension" },
    { key: "ischemic_heart_disease", label: "Ischemic Heart Disease" },
    { key: "asthma_copd", label: "Asthma/COPD" },
    { key: "stroke", label: "Stroke" },
    { key: "chronic_hepatitisb", label: "Chronic Hepatitis B" },
    { key: "msald", label: "MSALD" },
    { key: "chronickidneydisease", label: "Chronic Kidney Disease" },
    { key: "hiv_aids", label: "HIV/AIDS" },
    { key: "hypothyroidism", label: "Hypothyroidism" },
    { key: "others", label: "Others" },
  ];

  //  Submit handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);

      const payload = {
        user_id: userId,
        full_name: values?.full_name,
        mobile: values?.mobile,
        alternate_mobile: values?.alternate_mobile,
        dob: values?.dob,
        gender: values?.gender,
        abha: values?.abha,
        abha_address: values?.abha_address,
        abha_status: values?.abha_status,
        state_lgd: values?.state_lgd,
        district_lgd: values?.district_lgd,
        subdistrict_lgd: "",
        pincode: values?.pincode,
        address: values?.address,
        marital_status: values?.marital_status,
        assessment: {
          // Habits
          habit_smoking: values?.habit_smoking,
          habit_smoking_duration: getDuration(values?.habit_smoking_duration),
          habit_smokeless: values?.habit_smokeless,
          habit_smokeless_duration: getDuration(values?.habit_smokeless_duration),
          habit_betelnut_with_tobacco: values?.habit_betelnut_with_tobacco,
          habit_betelnut_with_tobacco_duration: getDuration(values?.habit_betelnut_with_tobacco_duration),
          habit_alcohol: values?.habit_alcohol,
          habit_alcohol_duration: getDuration(values?.habit_alcohol_duration),

          // Comorbidities
          como_diabetes: values?.como_diabetes,
          como_diabetes_duration: getDuration(values?.como_diabetes_duration),
          como_hypertension: values?.como_hypertension,
          como_hypertension_duration: getDuration(values?.como_hypertension_duration),
          como_ischemic_heart_disease: values?.como_ischemic_heart_disease,
          como_ischemic_heart_disease_duration: getDuration(values?.como_ischemic_heart_disease_duration),
          como_asthma_comorbidities_copd: values?.como_asthma_copd,
          como_asthma_comorbidities_copd_duration: getDuration(values?.como_asthma_copd_duration),
          como_stroke: values?.como_stroke,
          como_stroke_duration: getDuration(values?.como_stroke_duration),
          como_chronic_hepatitisb: values?.como_chronic_hepatitisb,
          como_chronic_hepatitisb_duration: getDuration(values?.como_chronic_hepatitisb_duration),
          como_msald: values?.como_msald,
          como_msald_duration: getDuration(values?.como_msald_duration),
          como_hiv_aids: values?.como_hiv_aids,
          como_hiv_aids_duration: getDuration(values?.como_hiv_aids_duration),
          como_hypothyroidism: values?.como_hypothyroidism,
          como_hypothyroidism_duration: getDuration(values?.como_hypothyroidism_duration),
          como_others: values?.como_others,
          como_others_duration: getDuration(values?.como_others_duration),

          // Family Cancer History
          history_family_cancer: values?.history_family_cancer,
          history_site_of_tumor:
            values?.history_family_cancer === "yes" ? values?.history_site_of_tumor : "",
          history_relation_of_tumor:
            values?.history_family_cancer === "yes"
              ? values?.history_relation === "Other"
                ? values?.history_specify_relation
                : values?.history_relation
              : "",
        },
      };

      const response = await authHttp.post(apisPath?.patientRegistration, payload);

      if (response?.data?.success) {
        setAlertState({
          open: true,
          title: "Patient Registered Successfully",
          btnTxt: "CLOSE",
          status: "fail",
        });

        navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title: error?.response?.data?.message,
        btnTxt: "CLOSE",
        status: "fail",
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getPatientRegistrationInitialValues(abhaProfile)}
      validationSchema={patientRegistrationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
        <Form>
          <Container>
            <div className="patient-registration">
              <div className="header">
                <h2>Patient Registration</h2>
              </div>

              {/* Patient Details */}
              <section className="patient-card">
                <h4>Patient Details</h4>
                <div className="patient-form-row">
                  <TextField label="Full Name" value={values?.full_name} fullWidth disabled />
                  <TextField
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={values?.dob}
                    fullWidth
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl fullWidth disabled>
                    <InputLabel>Gender</InputLabel>
                    <Select value={values?.gender} label="Gender">
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Mobile Number" value={values?.mobile} fullWidth disabled />
                  {/* alternate mobile field added */}
                  <TextField
                    label="Alternate Mobile Number"
                    name="alternate_mobile"
                    value={values?.alternate_mobile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!editable}
                    // error={touched.alternate_mobile && Boolean(errors.alternate_mobile)}
                    // helperText={touched.alternate_mobile && errors.alternate_mobile}
                    fullWidth
                  />
                  <TextField label="ABHA ID" value={formatAbhaId(values?.abha)} fullWidth disabled />
                  <TextField label="Address" value={values?.address} fullWidth disabled />

                  <FormControl
                    fullWidth
                    disabled={!editable}
                    error={touched.marital_status && Boolean(errors.marital_status)}
                  >
                    <InputLabel>Marital Status</InputLabel>
                    <Select
                      name="marital_status"
                      value={values?.marital_status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Marital Status"
                    >
                      <MenuItem value="Single">Single</MenuItem>
                      <MenuItem value="Married">Married</MenuItem>
                    </Select>
                    {touched.marital_status && errors.marital_status && (
                      <FormHelperText>{errors.marital_status}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </section>

              {/* Habits */}
              <section className="patient-card habits-section">
                <h4>Habits</h4>
                {habitsConfig.map((habit) => {
                  const fieldName = `habit_${habit.key}`;
                  const durationName = `${fieldName}_duration`;
                  return (
                    <div className="habit-row" key={habit.key}>
                      <label className="habit-label">{habit.label}</label>
                      <div className="radio-group">
                        {["yes", "no", "unknown"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name={fieldName}
                              value={opt}
                              checked={values[fieldName] === opt}
                              onChange={(e) => {
                                setFieldValue(fieldName, e.target.value);
                                setFieldValue(durationName, "");
                              }}
                              disabled={!editable}
                            />{" "}
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </label>
                        ))}
                      </div>
                      <TextField
                        name={durationName}
                        placeholder="Duration (Months)"
                        variant="outlined"
                        size="small"
                        value={values[durationName]}
                        onChange={(e) => {
                          // Allow only digits
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          handleChange({
                            target: { name: durationName, value },
                          });
                        }}
                        onBlur={handleBlur}
                        disabled={!editable || values[fieldName] !== "yes"}
                        // error={touched[durationName] && Boolean(errors[durationName])}
                        // helperText={touched[durationName] && errors[durationName]}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          maxLength: 3,
                        }}
                        type="text" // use text to avoid browser quirks with number inputs
                      />
                    </div>
                  );
                })}
              </section>

              {/* Comorbidities */}
              <section className="patient-card comorb-section">
                <h4>Comorbidities</h4>
                {comorbiditiesConfig.map((item) => {
                  const fieldName = `como_${item.key}`;
                  const durationName = `${fieldName}_duration`;
                  return (
                    <div className="comorb-row" key={item.key}>
                      <label className="comorb-label">{item.label}</label>
                      <div className="radio-group">
                        {["yes", "no"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name={fieldName}
                              value={opt}
                              checked={values[fieldName] === opt}
                              onChange={(e) => {
                                setFieldValue(fieldName, e.target.value);
                                setFieldValue(durationName, "");
                              }}
                              disabled={!editable}
                            />{" "}
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </label>
                        ))}
                      </div>
                      <TextField
                        name={durationName}
                        placeholder="Duration (Months)"
                        variant="outlined"
                        size="small"
                        value={values[durationName]}
                        onChange={(e) => {
                          // Allow only digits and limit to 3 characters
                          const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
                          handleChange({
                            target: { name: durationName, value },
                          });
                        }}
                        onBlur={handleBlur}
                        disabled={!editable || values[fieldName] !== "yes"}
                        // error={touched[durationName] && Boolean(errors[durationName])}
                        // helperText={touched[durationName] && errors[durationName]}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          maxLength: 3,
                        }}
                        type="text"
                      />
                    </div>
                  );
                })}
              </section>

              {/* Family History */}
              <section className="patient-card">
                <h4>History of Family Cancer</h4>
                <div className="radio-group">
                  {["yes", "no", "unknown"].map((opt) => (
                    <label key={opt}>
                      <input
                        type="radio"
                        name="history_family_cancer"
                        value={opt}
                        checked={values?.history_family_cancer === opt}
                        onChange={(e) => {
                          setFieldValue("history_family_cancer", e.target.value);
                          setFieldValue("history_site_of_tumor", "");
                          setFieldValue("history_relation", "");
                          setFieldValue("history_specify_relation", "");
                        }}
                        disabled={!editable}
                      />{" "}
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </label>
                  ))}
                </div>

                {values?.history_family_cancer === "yes" && (
                  <div className="row mt-3">
                    <div className="col-md-3">
                      <TextField
                        label="Site of Tumor"
                        name="history_site_of_tumor"
                        value={values?.history_site_of_tumor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!editable}
                        // error={touched.history_site_of_tumor && Boolean(errors.history_site_of_tumor)}
                        // helperText={touched.history_site_of_tumor && errors.history_site_of_tumor}
                        fullWidth
                      />
                    </div>
                    <div className="col-md-3">
                      <FormControl
                        fullWidth
                        disabled={!editable}
                        error={touched.history_relation && Boolean(errors.history_relation)}
                      >
                        <InputLabel>Relation</InputLabel>
                        <Select
                          name="history_relation"
                          value={values?.history_relation}
                          onChange={(e) => {
                            setFieldValue("history_relation", e.target.value);
                            if (e.target.value !== "Other") setFieldValue("history_specify_relation", "");
                          }}
                          onBlur={handleBlur}
                          label="Relation"
                        >
                          <MenuItem value="First Degree">First Degree</MenuItem>
                          <MenuItem value="Second Degree">Second Degree</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {/* {touched.history_relation && errors.history_relation && (
                          <FormHelperText>{errors.history_relation}</FormHelperText>
                        )} */}
                      </FormControl>
                    </div>
                    <div className="col-md-3">
                      <TextField
                        label="Specify Relation"
                        name="history_specify_relation"
                        value={values?.history_specify_relation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!editable || values?.history_relation !== "Other"}
                        // error={touched.history_specify_relation && Boolean(errors.history_specify_relation)}
                        // helperText={touched.history_specify_relation && errors.history_specify_relation}
                        fullWidth
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* Submit */}
              <div className="footer">
                <button type="submit" className="next-btn" disabled={isSubmitting}>
                  Submit →
                </button>
              </div>
            </div>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default PatientRegistration;
